import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { spacing16x, spacing8x } from "@airhelp/design-tokens";
import { HStack, useDisclosure, Text } from "@chakra-ui/react";

import airaloHeroSmall from "assets/images/perks/hero-airalo-small.png";
import airaloHero from "assets/images/perks/hero-airalo.png";
import rentalCarsHeroSmall from "assets/images/perks/hero-rentalcars-small.png";
import rentalCarsHero from "assets/images/perks/hero-rentalcars.png";
import airportTransfersHeroSmall from "assets/images/perks/hero-suntransfers-small.png";
import airportTransfersHero from "assets/images/perks/hero-suntransfers.png";
import airaloLogo from "assets/images/perks/logo-airalo.svg";
import carRentalLogo from "assets/images/perks/logo-rentalcars.svg";
import airportTransfersLogo from "assets/images/perks/logo-suntransfers.svg";
import { UserPolicy } from "models/UserPolicy";

import PerkBanner from "./PerkBanner";
import PerkDrawer from "./PerkDrawer";
import AirportTransfer from "./PerkDrawer/ServiceContent/AirportTransfer";
import CarRental from "./PerkDrawer/ServiceContent/CarRental";
import ESim from "./PerkDrawer/ServiceContent/ESim";

type IComponent = {
  userPolicy: UserPolicy;
};

type PerkService = {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  content: JSX.Element;
  imageText: string;
  description: string;
  imageSrc: string;
  imageSmallSrc: string;
  logoSrc: string;
  discount: number | null;
  dataTestId: string;
};

type Services = "eSim" | "carRental" | "airportTransfer";

type PerkServicesConfig = { [Service in Services]: PerkService | null };

const PerkServices: React.FC<IComponent> = ({ userPolicy }) => {
  const { t } = useTranslation();
  const { isOpen: isESimDrawerOpen, onOpen: onESimDrawerOpen, onClose: onESimDrawerClose } = useDisclosure();
  const {
    isOpen: isCarRentalDrawerOpen,
    onOpen: onCarRentalDrawerOpen,
    onClose: onCarRentalDrawerClose,
  } = useDisclosure();
  const {
    isOpen: isAirportTransfersDrawerOpen,
    onOpen: onAirportTransfersDrawerOpen,
    onClose: onAirportTransfersDrawerClose,
  } = useDisclosure();

  const perkServicesConfig: PerkServicesConfig = {
    eSim: userPolicy.perks.esim && {
      isOpen: isESimDrawerOpen,
      onClose: onESimDrawerClose,
      onOpen: onESimDrawerOpen,
      content: <ESim code={userPolicy.perks.esim?.code} discount={userPolicy.perks.esim?.discount} />,
      imageText: t("dashboard.perks.esim.esim_cards"),
      description: t("dashboard.perks.esim.global_connectivity"),
      imageSrc: airaloHero,
      imageSmallSrc: airaloHeroSmall,
      logoSrc: airaloLogo,
      discount: userPolicy.perks.esim.discount,
      dataTestId: "esim-perk",
    },
    carRental: userPolicy.perks.carRental && {
      isOpen: isCarRentalDrawerOpen,
      onClose: onCarRentalDrawerClose,
      onOpen: onCarRentalDrawerOpen,
      content: <CarRental code={userPolicy.perks.carRental.code} discount={userPolicy.perks.carRental.discount} />,
      imageText: t("dashboard.perks.car_rentals.car_rental"),
      description: t("dashboard.perks.car_rentals.great_value_wheels"),
      imageSrc: rentalCarsHero,
      imageSmallSrc: rentalCarsHeroSmall,
      logoSrc: carRentalLogo,
      discount: userPolicy.perks.carRental.discount,
      dataTestId: "car-rental-perk",
    },
    airportTransfer: userPolicy.perks.airportTransfer && {
      isOpen: isAirportTransfersDrawerOpen,
      onClose: onAirportTransfersDrawerClose,
      onOpen: onAirportTransfersDrawerOpen,
      content: <AirportTransfer />,
      imageText: t("dashboard.perks.airport_transfer.airport_transfers"),
      description: t("dashboard.perks.airport_transfer.low_cost_pick_ups"),
      imageSrc: airportTransfersHero,
      imageSmallSrc: airportTransfersHeroSmall,
      logoSrc: airportTransfersLogo,
      discount: null,
      dataTestId: "airport-transfer-perk",
    },
  };

  return (
    <>
      <Text color="primary.900" fontSize="md" fontWeight="medium" alignSelf="flex-start" order={3} mb={-8}>
        {t("dashboard.traveling_abroad_we_got_something")}
      </Text>
      <HStack
        width={{ base: `calc(100% + ${spacing8x})`, lg: `calc(100% + ${spacing16x})` }}
        order={4}
        spacing={4}
        alignItems="stretch"
        overflowX="auto"
        px={{ base: 4, lg: 8 }}
        ml={{ base: -4, lg: -8 }}
        sx={{
          scrollbarWidth: "none",
          msOverflowStyle: "-ms-autohiding-scrollbar",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {Object.values(perkServicesConfig).map(
          (perk, id) =>
            perk && (
              <Fragment key={`perk-service-${id}`}>
                <PerkBanner
                  imageSrc={perk.imageSmallSrc}
                  imageText={perk.imageText}
                  logoSrc={perk.logoSrc}
                  description={perk.description}
                  key={`${perk.dataTestId}-banner`}
                  onClick={perk.onOpen}
                  discount={perk.discount}
                  dataTestId={perk.dataTestId}
                />
                <PerkDrawer
                  isOpen={perk.isOpen}
                  onClose={perk.onClose}
                  content={perk.content}
                  imageText={perk.imageText}
                  imageSrc={perk.imageSrc}
                  dataTestId={perk.dataTestId}
                  key={`${perk.dataTestId}-drawer`}
                />
              </Fragment>
            )
        )}
      </HStack>
    </>
  );
};

export default PerkServices;
